import { useCallback, useEffect, useState } from 'react';

import { Button } from '../shared/components/Button';
import { useTranslation } from '../shared/i18n/useTranslation';

// ============================================================
// Lume / TEACare PWA — InstallPwaPrompt
// ============================================================
// PWA install prompt discreto, no rodapé.
//
// Regras:
//   - Não aparece se o app já roda como instalado (display: standalone).
//   - Captura `beforeinstallprompt` (Chromium / Android / Edge).
//   - Em iOS Safari, mostra fallback textual ("Compartilhar > Adicionar
//     à Tela de Início") quando o evento não vier — só após >= 2 visitas.
//   - Dispense persistente em localStorage por 30 dias.
//
// Princípios alinhados:
//   - 1: pessoa autista é sujeito — UI calma, dispensável, não intrusiva.
//   - 7: acessibilidade universal — funciona em qualquer dispositivo.
// ============================================================

const STORAGE_DISMISS_KEY = 'lume.install.dismissed';
const STORAGE_VISITS_KEY = 'lume.install.visits';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MIN_VISITS = 2;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari standalone flag
  return Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as Window & { MSStream?: unknown }).MSStream;
}

function isDismissed(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function persistDismiss(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_DISMISS_KEY, String(Date.now()));
  } catch {
    // localStorage off — silencioso.
  }
}

function bumpVisitsAndCheck(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_VISITS_KEY);
    const current = Number(raw) || 0;
    const next = current + 1;
    window.localStorage.setItem(STORAGE_VISITS_KEY, String(next));
    return next >= MIN_VISITS;
  } catch {
    return false;
  }
}

export interface InstallPwaPromptProps {
  /**
   * Posicionamento. Em `Home` queremos `inline` (centrado no flow).
   * Em `AppShell` queremos `fixed` (rodapé discreto).
   */
  variant?: 'inline' | 'fixed';
}

export function InstallPwaPrompt({ variant = 'inline' }: InstallPwaPromptProps) {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (isDismissed()) return;

    const visitsOk = bumpVisitsAndCheck();
    if (!visitsOk) return;

    const onBefore = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBefore);

    // iOS Safari não dispara beforeinstallprompt — fallback textual.
    if (isIos()) {
      setShowIosTip(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBefore);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'dismissed') persistDismiss();
    } catch {
      // se algo falhar, registra dismiss para não insistir.
      persistDismiss();
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    persistDismiss();
    setVisible(false);
    setShowIosTip(false);
  }, []);

  // Sem nada para mostrar.
  if (!visible && !showIosTip) return null;

  const containerClass =
    variant === 'fixed'
      ? 'fixed bottom-4 left-1/2 z-30 w-[min(92%,32rem)] -translate-x-1/2 rounded-xl border border-brand-100 bg-bg-surface p-4 shadow-elev1'
      : 'mt-6 w-full rounded-xl border border-brand-100 bg-brand-50/40 p-4';

  // Caso 1: temos beforeinstallprompt -> botão Install + Dismiss.
  if (visible && deferredPrompt) {
    return (
      <aside
        aria-label={t('install.title')}
        data-testid="install-pwa-prompt"
        className={containerClass}
      >
        <p className="font-semibold text-fg-DEFAULT">{t('install.title')}</p>
        <p className="mt-1 text-sm text-fg-muted">{t('install.body')}</p>
        <div className="mt-3 flex flex-wrap gap-3">
          <Button variant="primary" size="n2" onClick={handleInstall} data-testid="install-pwa-cta">
            {t('install.cta.install')}
          </Button>
          <Button variant="ghost" size="n2" onClick={handleDismiss}>
            {t('install.cta.dismiss')}
          </Button>
        </div>
      </aside>
    );
  }

  // Caso 2: iOS Safari fallback textual.
  if (showIosTip) {
    return (
      <aside
        aria-label={t('install.title')}
        data-testid="install-pwa-ios-tip"
        className={containerClass}
      >
        <p className="font-semibold text-fg-DEFAULT">{t('install.title')}</p>
        <p className="mt-1 text-sm text-fg-muted">{t('install.ios.intro')}</p>
        <div className="mt-3 flex gap-3">
          <Button variant="ghost" size="n2" onClick={handleDismiss}>
            {t('install.ios.dismiss')}
          </Button>
        </div>
      </aside>
    );
  }

  return null;
}
